import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, makeStateKey, signal, TransferState } from '@angular/core';
import { catchError, delay, finalize, first, map, Observable, of, retry, Subject } from 'rxjs';
import { Token } from '../../../interfaces/token';
import { ApiConfigService } from '../../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../../ssr-services/cache/cache.service';
import { readJwtPayloadExpSeconds } from '../../../utils/read-jwt-payload-exp';
import { StorageKeys, StorageService } from '../storage/storage.service';

export const SOCIAL_AUTH_TOKEN = makeStateKey<Token | null>('SOCIAL_AUTH_TOKEN');
export const SOCIAL_AUTH_ERROR = makeStateKey<string | null>('SOCIAL_AUTH_ERROR');

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authStateInitialized = false;

  public token = signal<null | Token>(null);

  public isAuthenticated = computed(() => !!this.token());

  protected readonly storageService = inject(StorageService);

  protected cacheService = inject(CacheService);

  protected http = inject(HttpClient);

  protected apiConfig = inject(ApiConfigService);

  public isRefreshingToken = signal(false);

  public refreshTokenReplaySubject = new Subject<Token | false>();

  public transferState = inject(TransferState);

  public authenticateWithToken(token: Token): void {
    this.authStateInitialized = true;
    this.token.set(token);
    this.storageService.setKey(StorageKeys.Token, token);
    this.storageService.removeKey(StorageKeys.SelectedLanguage);
  }

  public hasRefreshTokenExpired(): boolean {
    const token = this.token();

    if (!token) {
      return true;
    }

    const today = new Date();
    const refreshTokenTime = new Date(token.refreshTokenExpiresAt);

    return refreshTokenTime.getTime() <= today.getTime();
  }

  public hasTokenExpired(): boolean {
    const token = this.token();

    if (!token) {
      return true;
    }

    const jwtExpSec = readJwtPayloadExpSeconds(token.token);
    if (jwtExpSec != null && Date.now() >= jwtExpSec * 1000) {
      return true;
    }

    const today = new Date();
    const tokenExpireTime = new Date(token.expiresAt);

    return tokenExpireTime.getTime() <= today.getTime();
  }

  /**
   * Initializes the auth state from storage service
   * @returns
   */
  public initAuthSate(): boolean {
    if (this.authStateInitialized) {
      return false;
    }

    const token = this.storageService.getKey<Token>(StorageKeys.Token);

    if (token) {
      this.token.set(token);
    }

    this.authStateInitialized = true;
    return true;
  }

  public checkSocialUserTransferState(): void {
    const token = this.transferState.get(SOCIAL_AUTH_TOKEN, null);

    if (!token) {
      return;
    }

    this.authenticateWithToken(token);
  }

  /**
   * Authenticates with email and password
   * @param email
   * @param password
   * @returns
   */
  public signIn(email: string, password: string): Observable<Token> {
    const cacheContent = this.cacheService.cachedContent();

    return this.http
      .post<Token>(this.apiConfig.url('subscribers/signin'), {
        email,
        password,
        customerId: cacheContent?.cache.customerId,
      })
      .pipe(
        map((token) => {
          this.authenticateWithToken(token);
          return token;
        })
      );
  }

  /**
   * Handles referral code
   * @param referralCode
   * @returns
   */
  public handleReferralCode(referralCode: string): Observable<unknown> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.token()?.token}`,
    });

    return this.http
      .post(this.apiConfig.url(`subscribers/signin-referral/${referralCode}`), null, { headers })
      .pipe(
        map((result) => {
          return result;
        }),
        catchError((error) => {
          console.error('Failed to submit referral code:', error);
          return of({
            success: false,
            error: error.message || 'Failed to submit referral code',
          });
        })
      );
  }

  /**
   * Signs the user out of the system
   * @returns
   */
  public signOut(): Observable<boolean> {
    const token = this.token();

    if (!token) {
      return of(false);
    }

    return this.http.post(this.apiConfig.url('subscribers/signout'), {}).pipe(
      map(() => true),
      catchError((error) => {
        console.warn('Signout API FAILED');
        console.error(error.error);
        return of(false);
      }),
      map((result) => {
        this.clearTokenState();
        return result;
      })
    );
  }

  /**
   * Clears referral code dismissal flag from sessionStorage
   * Called on logout to allow the dialog to show again on next login
   */
  private clearReferralCodeDismissalFlag(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('referralCodeDismissed');
    }
  }

  public clearTokenState(): void {
    this.token.set(null);
    this.storageService.clearAll();
    this.clearReferralCodeDismissalFlag();
  }

  /**
   * POST /subscribers/token/refreshToken
   * Refresh the JWT token using refresh token
   */
  fetchRefreshToken(): Observable<Token | false> {
    if (this.isRefreshingToken()) {
      return this.refreshTokenReplaySubject.asObservable().pipe(first(), delay(10));
    }

    if (this.hasRefreshTokenExpired()) {
      this.clearTokenState();
      return of(false);
    }

    this.isRefreshingToken.set(true);
    const token = this.token() as Token;

    return this.http
      .post<Token>(this.apiConfig.url('subscribers/token/refreshToken'), {
        keepMeLoggedIn: true,
        refreshToken: token.refreshToken,
      })
      .pipe(
        retry(1),
        catchError(() => of(false as const)),
        map((result) => {
          result ? this.authenticateWithToken(result) : this.clearTokenState();
          this.refreshTokenReplaySubject.next(result);
          return result;
        }),
        delay(10),
        finalize(() => this.isRefreshingToken.set(false))
      );
  }
}
